import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ROOT, api, query } from "@/api";
import { useOrgPath, useSession } from "@/session";
import { useApi } from "@/useApi";
import { Async, Button, GitMark, Pager, Table, panel, primaryButton } from "@/ui";

const STATUS = {
  not_indexed: ["Queued", "var(--text-muted)"],
  indexing: ["Indexing", "var(--text-secondary)"],
  indexed: ["Indexed", "var(--success)"],
  failed: ["Failed", "var(--danger)"],
};

const STEP = {
  checkout: "Checking out code",
  graph: "Reading code",
  chunks: "Preparing code",
  embed: "Preparing search",
  retry: "Retrying indexing",
};

const ERROR = {
  index_incomplete: "Indexing could not finish. Try again in a few minutes.",
  index_interrupted: "Indexing stopped when the worker restarted. Retry to continue.",
  installation_unavailable: "CodeNerve lost access to this repository. Reconnect GitHub in Settings, then retry.",
};

const indexedDateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

/* Where each step starts, in steps of four. Only embedding has a measured fraction (vectors
   stored over chunks to embed); the earlier steps have no size until they finish, so the bar
   waits at their start rather than inventing motion. */
const STEP_ORDER = { checkout: 0, graph: 1, chunks: 2, embed: 3, retry: 3 };
const STEPS = 4;
const count = new Intl.NumberFormat();

function IndexProgress({ repo }) {
  const step = STEP_ORDER[repo.indexStep] ?? 0;
  const measured = step === 3 && repo.indexProgress?.total > 0 ? repo.indexProgress : null;
  const fraction = measured ? measured.done / measured.total : 0;
  const percent = Math.round(((step + fraction) / STEPS) * 100);
  const detail = measured
    ? `${Math.round(fraction * 100)}% · ${count.format(measured.done)} of ${count.format(measured.total)}`
    : `Step ${step + 1} of ${STEPS}`;
  return (
    <span style={{ display: "grid", gap: "7px" }}>
      <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
        {STEP[repo.indexStep] ?? "Starting"} · {detail}
      </span>
      <span
        role="progressbar"
        aria-label="Indexing progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        style={{ position: "relative", width: "160px", height: "3px", borderRadius: "999px", background: "var(--border)" }}
      >
        <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${percent}%`, borderRadius: "999px", background: "var(--text-primary)", transition: "width 900ms var(--ease-out)" }} />
        <span aria-hidden="true" style={{ position: "absolute", top: "50%", left: `${percent}%`, width: "7px", height: "7px", borderRadius: "50%", background: "var(--text-primary)", transform: "translate(-50%, -50%)", transition: "left 900ms var(--ease-out)" }} />
      </span>
    </span>
  );
}

export function Repositories() {
  const orgPath = useOrgPath();
  const { organization } = useSession();
  const [offset, setOffset] = useState(0);
  const [retrying, setRetrying] = useState(null);
  const [retryError, setRetryError] = useState(null);
  const canRetry = organization?.role === "org_admin";

  const repositories = useApi(
    (signal) => api.get(`${orgPath}/repositories${query({ enabled: true, limit: 25, offset })}`, { signal }),
    [orgPath, offset],
    { skip: !orgPath },
  );

  useEffect(() => {
    if (!orgPath) return undefined;
    const timer = setInterval(repositories.reload, 10000);
    return () => clearInterval(timer);
  }, [orgPath, repositories.reload]);

  async function retry(repo) {
    if (retrying) return;
    setRetrying(repo.id);
    setRetryError(null);
    try {
      await api.post(`${orgPath}/repositories/${repo.id}/retry-index`);
      repositories.reload();
    } catch (error) {
      setRetryError({ id: repo.id, message: error.message });
    } finally {
      setRetrying(null);
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <div>
          <h1 style={{ font: "var(--text-h4)", color: "var(--text-primary)", margin: "0 0 5px" }}>
            Repositories
          </h1>
          <p style={{ font: "var(--text-body-14)", color: "var(--text-muted)", margin: 0 }}>
            Repositories CodeNerve can review. Indexing status refreshes automatically.
          </p>
        </div>
        {/* GitHub's App page, where an existing installation picks more repositories. */}
        {canRetry && (
          <a href={`${ROOT}/api/v1${orgPath}/github/install`} style={{ ...primaryButton, textDecoration: "none" }}>
            Add repositories
          </a>
        )}
      </div>

      <div style={{ ...panel, overflow: "hidden" }}>
        <Async state={repositories}>
          {(page) => (
            <>
              <Table
                minWidth="760px"
                empty={<>No repositories are connected. <Link to="/settings/providers">Connect GitHub in Settings.</Link></>}
                rows={page.items}
                columns={[
                  {
                    key: "repository",
                    header: "Repository",
                    render: (repo) => (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: "var(--text-primary)" }}>
                        <GitMark size={14} />{repo.name}
                      </span>
                    ),
                  },
                  {
                    key: "status",
                    header: "Index status",
                    render: (repo) => {
                      const [label, color] = STATUS[repo.indexStatus] ?? ["Unknown", "var(--text-muted)"];
                      return (
                        <span style={{ display: "grid", gap: "3px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", color }}>
                            {repo.indexStatus === "indexing" && (
                              <span aria-hidden="true" style={{ width: "16px", height: "16px", flex: "0 0 16px", border: "2px solid var(--border-strong)", borderBottomColor: "var(--text-primary)", borderRadius: "50%", animation: "cn-turn 0.8s linear infinite" }} />
                            )}
                            {label}
                          </span>
                          {repo.indexStatus === "indexing" && <IndexProgress repo={repo} />}
                          {repo.indexStatus === "failed" && (
                            <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
                              {ERROR[repo.indexError] ?? "Indexing failed. Please retry."}
                            </span>
                          )}
                        </span>
                      );
                    },
                  },
                  {
                    key: "indexedAt",
                    header: "Last indexed",
                    render: (repo) => repo.indexedAt ? (
                      <time dateTime={repo.indexedAt} style={{ color: "var(--text-secondary)" }}>
                        {indexedDateTime.format(new Date(repo.indexedAt))}
                      </time>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>Never</span>
                    ),
                  },
                  {
                    key: "action",
                    header: "Action",
                    align: "right",
                    render: (repo) => repo.indexStatus === "failed" ? (
                      <span style={{ display: "inline-grid", justifyItems: "end", gap: "5px" }}>
                        {canRetry ? (
                          <Button tone="secondary" disabled={Boolean(retrying)} onClick={() => retry(repo)}>
                            {retrying === repo.id ? "Retrying…" : "Retry"}
                          </Button>
                        ) : (
                          <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
                            Ask an admin to retry
                          </span>
                        )}
                        {retryError?.id === repo.id && (
                          <span role="alert" style={{ font: "var(--text-caption)", color: "var(--danger)" }}>
                            {retryError.message}
                          </span>
                        )}
                      </span>
                    ) : null,
                  },
                ]}
              />
              <Pager page={page} onChange={setOffset} />
            </>
          )}
        </Async>
      </div>
    </>
  );
}
